/**
 * ViewEditPage - Multi-step wizard for editing existing views
 *
 * Reuses the same wizard components as ViewCreatePage but pre-populates
 * with existing view data and uses update mutation instead of create.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import { useView, useUpdateView, useAffectedViews } from '@/hooks/useViews';
import { ImpactWarningDialog } from '@/components/views/ImpactWarningDialog';
import type { View, UpdateViewRequest } from '@/types/views';
import type { ViewFormData } from '@/pages/ViewCreatePage';
import { ViewWizardSteps } from '@/components/views/wizard/ViewWizardSteps';
import { BasicInfoStep } from '@/components/views/wizard/BasicInfoStep';
import { RuleBuilderStep } from '@/components/views/wizard/RuleBuilderStep';
import { ColumnConfigStep } from '@/components/views/wizard/ColumnConfigStep';
import { TransformationsStep } from '@/components/views/wizard/TransformationsStep';
import { RefreshSettingsStep } from '@/components/views/wizard/RefreshSettingsStep';
import { ReviewStep } from '@/components/views/wizard/ReviewStep';

const TOTAL_STEPS = 6;

const STEP_TITLES = [
  'Basic Information',
  'File Selection Rules',
  'Advanced Transformations',
  'Column Configuration',
  'Refresh Settings',
  'Review & Save',
];

const STEP_DESCRIPTIONS = [
  'Update the name and description for your view',
  'Modify which files should be included in this view',
  'Update optional WHERE, GROUP BY, and JOIN transformations',
  'Change which columns are included in the view',
  'Configure how and when this view refreshes',
  'Review your changes and save the view',
];

/**
 * Transform API View object to ViewFormData
 *
 * IMPORTANT: Backend doesn't include id fields for transformations (frontend-only for React keys).
 * We must generate unique IDs to prevent React rendering bugs where deleting one item deletes multiple.
 *
 * Backend entities WITHOUT ids:
 * - JoinConfig, JoinCondition
 * - WhereCondition, HavingCondition
 * - AggregateColumn
 *
 * Backend entities WITH ids:
 * - ViewRule, ViewColumn (these come from DB tables)
 */
const transformViewToFormData = (view: View): ViewFormData => {
  const timestamp = Date.now();

  // Generate unique IDs for WHERE conditions
  const whereConditionsWithIds = (view.where_conditions || []).map((condition, index) => ({
    ...condition,
    id: condition.id || `where-${timestamp}-${index}`,
  }));

  // Generate unique IDs for HAVING conditions
  const havingConditionsWithIds = (view.having_conditions || []).map((condition, index) => ({
    ...condition,
    id: condition.id || `having-${timestamp}-${index}`,
  }));

  // Generate unique IDs for GROUP BY aggregate columns
  const groupByWithIds = view.group_by
    ? {
        ...view.group_by,
        aggregate_columns: view.group_by.aggregate_columns.map((agg: any, index: number) => ({
          ...agg,
          id: agg.id || `agg-${timestamp}-${index}`,
        })),
      }
    : null;

  // Generate unique IDs for JOINs and their conditions
  const joinsWithIds = (view.joins || []).map((join, joinIndex) => ({
    ...join,
    id: join.id || `join-${timestamp}-${joinIndex}`,
    // Backend stores the target name in target_view_id for both datasets and views.
    // The frontend Select for datasets reads target_dataset_id, so copy it over.
    target_dataset_id: join.source_type === 'dataset' ? (join.target_dataset_id || join.target_view_id) : join.target_dataset_id,
    on_conditions: (join.on_conditions || []).map((condition, condIndex) => ({
      ...condition,
      id: condition.id || `join-cond-${timestamp}-${joinIndex}-${condIndex}`,
    })),
  }));

  return {
    name: view.name,
    description: view.description || '',
    tags: view.tags || [],
    rules: view.rules || [],
    rule_logic: view.rule_logic || 'AND',
    select_all_columns: view.select_all_columns ?? (view.columns && view.columns.length > 0 ? false : true),
    columns: view.columns || [],
    where_conditions: whereConditionsWithIds,
    group_by: groupByWithIds,
    having_conditions: havingConditionsWithIds,
    joins: joinsWithIds,
    is_auto_refresh: view.is_auto_refresh ?? false,
    refresh_schedule: view.refresh_schedule || 'manual',
    view_type: view.view_type,
    partition_columns: view.partition_columns,
  };
};

/**
 * ViewEditPage Component
 */
export const ViewEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: view, isLoading, error } = useView(id!);
  const updateView = useUpdateView();
  const { data: affectedViews } = useAffectedViews(id);

  const [currentStep, setCurrentStep] = useState(1);
  // Lazy-initialized from `view` in case it's already available on the very
  // first render (e.g. a warm query cache), so the render-phase sync below —
  // which only reacts to `view` *changing* — doesn't miss the case where it
  // never changes because it was there from the start.
  const [formData, setFormData] = useState<ViewFormData | null>(() =>
    view ? transformViewToFormData(view) : null
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [showImpactWarning, setShowImpactWarning] = useState(false);

  /**
   * Re-initialize form data if `view` arrives (or changes) after mount.
   *
   * "Has formData ever been seeded" is history-dependent — once set it must
   * stay set even if `view` later changes reference (e.g. a background
   * refetch), so it can't be recomputed fresh each render. Adjusted during
   * render (not an effect) via a previous-value mirror of `view?.id` (a
   * stable string primitive): converges on the very next render, so this
   * fires exactly once, the moment the view for this id first becomes
   * available (matching the original effect's `if (view && !formData)`
   * guard).
   */
  const [prevViewId, setPrevViewId] = useState(view?.id);
  if (view?.id !== prevViewId) {
    setPrevViewId(view?.id);
    if (view && !formData) {
      setFormData(transformViewToFormData(view));
    }
  }

  /**
   * Handle cancel - navigate back to details page
   */
  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        navigate(`/views/${id}`);
      }
    } else {
      navigate(`/views/${id}`);
    }
  };

  /**
   * Update form data and mark as changed
   */
  const updateFormData = (updates: Partial<ViewFormData>) => {
    setFormData((prev) => (prev ? { ...prev, ...updates } : null));
    setHasChanges(true);
  };

  /**
   * Validate current step before proceeding
   */
  const validateStep = (step: number): { isValid: boolean; message?: string } => {
    if (!formData) return { isValid: false };

    switch (step) {
      case 1: {
        // Basic info: name is required
        if (!formData.name.trim()) {
          return { isValid: false, message: 'View name is required' };
        }
        if (formData.name.length < 3 || formData.name.length > 100) {
          return { isValid: false, message: 'View name must be between 3 and 100 characters' };
        }
        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(formData.name)) {
          return {
            isValid: false,
            message: 'View name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)',
          };
        }
        return { isValid: true };
      }
      case 2: {
        // Rules: at least one rule required
        if (formData.rules.length === 0) {
          return { isValid: false, message: 'At least one file selection rule is required' };
        }
        return { isValid: true };
      }
      case 3: {
        // Transformations: optional, always valid
        return { isValid: true };
      }
      case 4: {
        // Columns: if not select_all, must have at least one column
        if (!formData.select_all_columns && formData.columns.length === 0) {
          return { isValid: false, message: 'At least one column must be selected' };
        }
        return { isValid: true };
      }
      case 5: {
        // Refresh settings: always valid (has defaults)
        return { isValid: true };
      }
      case 6: {
        // Review: validate entire form
        const step1 = validateStep(1);
        if (!step1.isValid) return step1;
        const step2 = validateStep(2);
        if (!step2.isValid) return step2;
        const step3 = validateStep(3);
        if (!step3.isValid) return step3;
        return { isValid: true };
      }
      default:
        return { isValid: false };
    }
  };

  /**
   * Handle next step
   */
  const handleNext = () => {
    const validation = validateStep(currentStep);
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  /**
   * Handle previous step
   */
  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  /**
   * Handle save view submission
   */
  const handleSave = async () => {
    if (!formData || !id) return;

    const validation = validateStep(6);
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }

    // Check if there are affected views and show warning
    if (affectedViews && affectedViews.length > 0 && !showImpactWarning) {
      setShowImpactWarning(true);
      return;
    }

    // Proceed with save
    await performSave();
  };

  /**
   * Perform the actual save operation
   */
  const performSave = async () => {
    if (!formData || !id) return;

    try {
      // Full replacement: every field is sent on every save, regardless of user changes.
      // Field presence is not a change signal on the server. Previously (#1680), the
      // server guarded on request.rules being present, which was always true, so a user
      // editing only description would be falsely rejected. The server now compares
      // against the persisted set (_request_rules_differ_from_persisted).
      const request: UpdateViewRequest = {
        name: formData.name,
        description: formData.description || undefined,
        tags: formData.tags,
        rules: formData.rules,
        rule_logic: formData.rule_logic,
        select_all_columns: formData.select_all_columns,
        columns: formData.select_all_columns ? [] : formData.columns,
        where_conditions: formData.where_conditions,
        group_by: formData.group_by || undefined,
        having_conditions: formData.having_conditions,
        joins: formData.joins,
        view_type: formData.view_type,
        is_auto_refresh: formData.is_auto_refresh,
        refresh_schedule: formData.refresh_schedule,
      };

      const updatedView = await updateView.mutateAsync({ id, data: request });
      toast({
        title: 'View Updated',
        description: `View "${updatedView.name}" has been updated successfully.`,
      });
      setHasChanges(false);
      setShowImpactWarning(false);
      navigate(`/views/${id}`);
    } catch (error: any) {
      toast({
        title: 'Failed to Update View',
        description: error?.response?.data?.detail || error?.response?.data?.message || error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      setShowImpactWarning(false);
    }
  };

  /**
   * Render current step component
   */
  const renderStep = () => {
    if (!formData) return null;

    switch (currentStep) {
      case 1:
        return <BasicInfoStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return (
          <RuleBuilderStep
            formData={formData}
            updateFormData={updateFormData}
            excludeViewNames={view?.name ? [view.name] : undefined}
          />
        );
      case 3:
        return <TransformationsStep formData={formData} updateFormData={updateFormData} currentViewId={view?.id} />;
      case 4:
        return <ColumnConfigStep formData={formData} updateFormData={updateFormData} />;
      case 5:
        return <RefreshSettingsStep formData={formData} updateFormData={updateFormData} />;
      case 6:
        return (
          <ReviewStep
            formData={formData}
            onEdit={setCurrentStep}
            onCreate={handleSave}
            isLoading={updateView.isPending}
            isEditMode={true}
          />
        );
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-9 w-64" />
              <Skeleton className="h-5 w-96" />
            </div>
            <Skeleton className="h-10 w-24" />
          </div>
          <Skeleton className="h-2 w-full" />
          <Card className="p-6 min-h-[500px]">
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !view) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading View</AlertTitle>
          <AlertDescription>
            {error?.message || 'View not found. It may have been deleted or you may not have access to it.'}
          </AlertDescription>
        </Alert>
        <div className="mt-6">
          <Button onClick={() => navigate('/views')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Views
          </Button>
        </div>
      </div>
    );
  }

  // #1575: script-created views have no builder rules, so the wizard cannot
  // represent them -- transformViewToFormData reads only builder config and
  // never sql_definition. Reachable by direct URL even with the list's Edit
  // button disabled, so the route is guarded here too.
  if (view.is_script_managed) {
    return (
      <div className="container mx-auto py-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Script-maintained view</AlertTitle>
          <AlertDescription>
            <strong>{view.name}</strong> is maintained by a materialize script rather than the
            view builder, so it cannot be edited here. Its definition changes when that script
            is re-run.
          </AlertDescription>
        </Alert>
        <div className="mt-6">
          <Button onClick={() => navigate(`/views/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to View
          </Button>
        </div>
      </div>
    );
  }

  // No form data yet (shouldn't happen after the render-phase initializer above)
  if (!formData) {
    return null;
  }

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="container mx-auto py-8">
      {/* Impact Warning Dialog */}
      {affectedViews && affectedViews.length > 0 && (
        <ImpactWarningDialog
          isOpen={showImpactWarning}
          affectedViews={affectedViews}
          changeType="edit"
          viewName={view?.name}
          onConfirm={performSave}
          onCancel={() => setShowImpactWarning(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Edit View: {view.name}</h1>
          <p className="text-muted-foreground mt-1">
            Step {currentStep} of {TOTAL_STEPS}: {STEP_TITLES[currentStep - 1]}
          </p>
        </div>
        <Button variant="ghost" onClick={handleCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {/* Warning Alerts */}
      <div className="space-y-3 mb-6">
        {view.view_type === 'materialized' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Materialized View</AlertTitle>
            <AlertDescription>
              This view is materialized. Saving changes will invalidate the materialized data and require
              re-materialization, which may take several minutes.
            </AlertDescription>
          </Alert>
        )}
        {view.is_auto_refresh && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Auto-Refresh Enabled</AlertTitle>
            <AlertDescription>
              Changes to rules or columns may affect which files are included in future automatic refreshes.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <Progress value={progress} className="h-2" />
        <ViewWizardSteps currentStep={currentStep} steps={STEP_TITLES} />
      </div>

      {/* Step Content */}
      <Card className="p-6 mb-6 min-h-[500px]">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{STEP_TITLES[currentStep - 1]}</h2>
          <p className="text-sm text-muted-foreground">{STEP_DESCRIPTIONS[currentStep - 1]}</p>
        </div>
        {renderStep()}
      </Card>

      {/* Navigation - Hide on final step (Review has its own buttons) */}
      {currentStep < TOTAL_STEPS && (
        <div className="flex justify-between">
          <Button variant="outline" onClick={handlePrevious} disabled={currentStep === 1}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <Button onClick={handleNext} disabled={!validateStep(currentStep).isValid}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Previous button only on final step */}
      {currentStep === TOTAL_STEPS && (
        <div className="flex justify-start">
          <Button variant="outline" onClick={handlePrevious}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
        </div>
      )}
    </div>
  );
};
